'use client';

import { cn } from '@common/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactElement } from 'react';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export type TabContextType<T extends string = string> = {
  activeValue: T;
  onChange?: (value: T) => void;
  size?: 'sm' | 'lg';
  variant?: 'full' | 'flex';
};

export const TabContext = createContext<TabContextType<string> | undefined>(
  undefined,
);

export const useTabContext = <T extends string = string>() => {
  const context = useContext(TabContext);

  if (!context) {
    throw new Error('useTabContext must be used within a TabProvider');
  }

  return context as unknown as TabContextType<T>;
};

const tabItemVariants = cva(
  'flex items-center whitespace-nowrap bg-transparent transition-all gap-4',
  {
    variants: {
      size: {
        sm: 'px-16 h-53 typo-body4 min-w-60',
        lg: 'px-20 h-70 typo-body1 min-w-120',
      },
      variant: {
        full: 'flex-1 text-center justify-center',
        flex: 'text-center justify-center',
      },
      active: {
        true: 'text-Gray-black font-700',
        false: 'text-Gray-900 font-400 hover:border-Gray-500 hover:font-600',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed',
        false: 'cursor-pointer',
      },
    },
    defaultVariants: {
      size: 'sm',
      variant: 'full',
      active: false,
      disabled: false,
    },
    compoundVariants: [
      {
        active: true,
        size: 'sm',
        className: 'border-b-2 border-Gray-black relative z-10',
      },
      {
        active: true,
        size: 'lg',
        className: 'border-b-4 border-Gray-black relative z-10',
      },
    ],
  },
);

export interface TabItemProps extends VariantProps<typeof tabItemVariants> {
  /**
   * 탭 항목의 고유 값
   */
  value: string;
  /**
   * 아이콘 (선택 사항) - 오른쪽에 표시됨
   */
  icon?: React.ReactNode;
  /**
   * 비활성화 여부
   */
  disabled?: boolean;
  /**
   * 추가 스타일 클래스
   */
  className?: string;
  children: React.ReactNode;
}

function TabItem({
  value,
  icon,
  disabled = false,
  className,
  children,
}: TabItemProps) {
  const { activeValue, onChange, size, variant } = useTabContext();
  const isActive = activeValue === value;

  const handleClick = () => {
    if (!disabled) {
      onChange?.(value);
    }
  };

  return (
    <button
      type="button"
      className={cn(
        tabItemVariants({
          size,
          variant,
          active: isActive,
          disabled,
          className,
        }),
      )}
      onClick={handleClick}
      disabled={disabled}
      aria-selected={isActive}
      role="tab"
    >
      {children}
      {icon ? icon : null}
    </button>
  );
}

const tabVariants = cva(
  'relative overflow-x-auto hide-scrollbar border-b border-Gray-300',
  {
    variants: {
      size: {
        sm: 'h-53',
        lg: 'h-70',
      },
      variant: {
        full: 'flex w-full flex-nowrap',
        flex: 'flex w-full flex-nowrap',
      },
      overflow: {
        true: '',
      },
    },
    defaultVariants: {
      size: 'sm',
      variant: 'full',
      overflow: false,
    },
  },
);

export interface TabProps<T extends string = string>
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>,
    Omit<VariantProps<typeof tabVariants>, 'overflow'> {
  /**
   * 현재 활성화된 탭 값
   */
  value: T;
  /**
   * 탭 변경 시 호출되는 함수
   */
  onChange?: (value: T) => void;
  /**
   * 탭의 사이즈 (sm: 53px, lg: 70px)
   */
  size?: 'sm' | 'lg';
  /**
   * 탭의 타입
   */
  variant?: 'full' | 'flex';
  /**
   * 탭 컨텐츠
   */
  children: React.ReactNode;
  /**
   * 추가 스타일 클래스
   */
  className?: string;
}

export type TabComponent = {
  <T extends string = string>(props: TabProps<T>): ReactElement;
  Item: <V extends string>(
    props: Omit<TabItemProps, 'value'> & {
      value: V;
    },
  ) => ReactElement;
};

function TabBase<T extends string = string>({
  value,
  onChange,
  size = 'sm',
  variant = 'full',
  children,
  className,
  ...props
}: TabProps<T>) {
  const [isOverflow, setIsOverflow] = useState(false);
  const tabRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (tabRef.current) {
        const { scrollWidth, clientWidth } = tabRef.current;
        setIsOverflow(scrollWidth > clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [children]);

  const handleChange = (newValue: string) => {
    onChange?.(newValue as T);
  };

  return (
    <TabContext.Provider
      value={{
        activeValue: value,
        onChange: handleChange,
        size,
        variant,
      }}
    >
      <div
        ref={tabRef}
        className={cn(
          tabVariants({
            size,
            variant,
            overflow: isOverflow,
            className,
          }),
        )}
        role="tablist"
        {...props}
      >
        {children}
      </div>
    </TabContext.Provider>
  );
}

export const Tab = TabBase as unknown as TabComponent;
Tab.Item = TabItem;
